import React, {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Dialog} from '@pb/kit-component-dialog-v2';

import {BulkActionProgress} from './bulk-action-progress';

import type {ReactElement} from 'react';
import type {
    BulkActionIdSelector,
    BulkActionTaskSelector,
    GetProgress,
    SaveActionRequest,
    SetResultShown,
    SetTask,
    SetUuid,
    StopAction,
} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';

type ModalProps<SaveRequest> = {
    onCloseModal: (submit: boolean) => void,
    processAction: (saveRequest: SaveRequest, options?: ActionOptions) => Promise<string | undefined>,
};

type Props<SaveRequest> = {
    shown: boolean,
    onClose: () => void,
    children: (props: ModalProps<SaveRequest>) => ReactElement,
    width?: number,

    toolTask: string,
    toolName: string,
    progressLabel: string,

    taskSelector: BulkActionTaskSelector,
    uuidSelector: BulkActionIdSelector,

    setUuid: SetUuid,
    setTask: SetTask,
    setResultShown: SetResultShown,
    saveActionRequest: SaveActionRequest<SaveRequest>,
    getProgress: GetProgress,
    stopAction?: StopAction,

    stackOverflowMessage?: string,
    onDone?: () => void,
};

export const BulkActionTool = <SaveRequest, >({
    shown,
    onClose,
    children,
    width,
    onDone,
    toolTask,
    toolName,
    progressLabel,
    taskSelector,
    uuidSelector,
    setUuid,
    setTask,
    setResultShown,
    saveActionRequest,
    getProgress,
    stopAction,
    stackOverflowMessage = STACKOVERFLOW_MESSAGE,
}: Props<SaveRequest>) => {
    const dispatch = useDispatch();
    const createBackgroundTask = useBackgroundTaskManager();
    const startingRef = useRef(false);

    const {bulkOpen} = useEventTracking();
    const {bulkSubmit} = useEventTracking();

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current}) && current) {
            bulkOpen({toolName});
        }
    }, shown);

    const closeModal = useCallback((submit: boolean) => {
        if (!submit && startingRef.current) {
            return;
        }
        onClose();
        bulkSubmit({toolName, submit});
    }, [toolName, bulkSubmit, onClose]);

    const processAction = useCallback(async (saveRequest: SaveRequest, options?: ActionOptions) => {
        if (startingRef.current) {
            return 'This operation is already starting.';
        }
        startingRef.current = true;
        try {
            const response = await saveActionRequest(saveRequest, options);

            if (response.payload.success) {
                const uuid = response.payload.data;
                const backgroundTask = createBackgroundTask(
                    toolTask,
                    <BulkActionProgress
                        label={progressLabel}
                        taskSelector={taskSelector}
                        idSelector={uuidSelector}
                        getProgress={id => getProgress(id)}
                        stopAction={stopAction ? id => stopAction(id) : undefined}
                        setShown={setResultShown}
                        onDone={onDone}
                    />,
                    {maxStack: 1, stackOverflowMessage},
                );

                if (backgroundTask) {
                    dispatch(setUuid(uuid));
                    dispatch(setTask(backgroundTask));
                }
                closeModal(true);
                return undefined;
            }
            return getError({error: response.payload.data, separator: ' '});
        } catch {
            return 'Unable to start this operation. Please try again.';
        } finally {
            startingRef.current = false;
        }
    }, [
        saveActionRequest,
        toolTask,
        progressLabel,
        taskSelector,
        uuidSelector,
        getProgress,
        stopAction,
        setResultShown,
        onDone,
        stackOverflowMessage,
        setUuid,
        setTask,
        closeModal,
        createBackgroundTask,
        dispatch,
    ]);

    return (
        <Dialog
            shown={shown}
            setShown={() => closeModal(false)}
            wrapperProps={{style: {width: width ?? 600}}}>
            {children({onCloseModal: closeModal, processAction})}
        </Dialog>
    );
};
